import { BuildingDesign } from '@shared/models/design/building-design.model';
import { Storey } from '@shared/models/design/storey.model';
import { Point2D } from '@shared/models/geometry.model';
import { doBuildingSolidsIntersect, getGfaFromStoreys } from './shape.helpers';
import {
  applyGroundFootprintToEmptyStoreys,
  findEmptyBuildingToFill,
  getBuildingById,
  getBuildingStoreys,
  getEmptyBuildingPlacementOrigin,
  getSiblingActivitySourceStorey,
  isEmptyBuildingGeometry,
  isEmptyStoreysGeometry,
  seedGroundStoreyGeometry,
  setBuildingStoreys,
  setBuildingStoreysWithProgramSeed,
} from './storey.helpers';
import { guards } from '../typeguards.helpers';
import { generateRectangle } from './shape-templates.helpers';
import { createBuildingDesign } from './factories/design.factory';
import { createStoreys } from './factories/storey.factory';
import { designGuards } from './design-typeguards.helpers';
import {
  getCenter,
  isPolygon2D,
  movePoints,
  multiplyPoint2D,
} from '../geometry/geometry.helpers';

const DEFAULT_SCALE_TOLERANCE = 0.001;

export type ScaleBuildingToGfaStatus = 'applied' | 'overlap' | 'unchanged';

export type ScaleBuildingToGfaResult = {
  design: BuildingDesign;
  status: ScaleBuildingToGfaStatus;
};

const unchangedResult = (design: BuildingDesign): ScaleBuildingToGfaResult => ({
  design,
  status: 'unchanged',
});

/**
 * Building to receive a GFA edit. A drawn selected building is scaled in place.
 * Otherwise an empty selected building, or the first empty sibling when no
 * building is selected, is filled — same target as pen-close.
 */
const resolveGfaTargetBuildingId = (
  design: BuildingDesign,
  buildingId?: string,
): string | undefined => {
  const selected = getBuildingById(design, buildingId);
  if (buildingId && selected && !isEmptyBuildingGeometry(selected)) {
    return buildingId;
  }
  return findEmptyBuildingToFill(design, buildingId)?.id ?? buildingId;
};

/**
 * Check if a design (or a specific building) has no drawable footprint.
 * @param root {@link BuildingDesign} or {@link Storey} list to evaluate
 * @param buildingId Building to inspect; omitted = first building
 * @returns true when the target has no geometry
 */
export const isEmptyBuildingDesign = (
  root: BuildingDesign | Storey[],
  buildingId?: string,
): boolean => {
  if (guards.is.array(root)) {
    return isEmptyStoreysGeometry(root);
  }
  return isEmptyBuildingGeometry(getBuildingById(root, buildingId));
};

/**
 * Create a fresh design with a single square footprint and one storey sized to
 * the target GFA. Used when the user enters a GFA on a project that has no
 * geometry yet, so we have something to scale from going forward.
 * Keeps the current design's location and activities.
 * @param currentDesign - The current (empty) building design
 * @param targetGfa - The target GFA value in m²
 */
export const createSquareDesignFromGfa = (
  currentDesign: BuildingDesign,
  targetGfa: number,
): BuildingDesign => {
  const side = Math.sqrt(targetGfa);
  return createBuildingDesign({
    location: currentDesign.location,
    activities: currentDesign.activities,
    storeys: [{ shapes: [generateRectangle(side, side)] }],
  });
};

/**
 * One-storey square sized to {@link targetGfa}, used to fill an empty building
 * without replacing the rest of the design.
 */
const createSquareStoreysFromGfa = (
  targetGfa: number,
  origin: Point2D,
): Storey[] => {
  const side = Math.sqrt(targetGfa);
  const square = generateRectangle(side, side);
  const points = movePoints(square, origin);
  if (!isPolygon2D(points)) return [];
  return createStoreys([{ shapes: [points] }]);
};

/**
 * Put a square footprint on {@link buildingId} (or the first building).
 * Never replaces sibling buildings. Extra empty floors keep their slots and
 * share the target GFA so stacked storeys stay connected.
 */
const createSquareOnBuilding = (
  currentDesign: BuildingDesign,
  targetGfa: number,
  buildingId?: string,
): BuildingDesign => {
  const target = getBuildingById(currentDesign, buildingId);
  if (!target) {
    if (!currentDesign.buildings.length) {
      return createSquareDesignFromGfa(currentDesign, targetGfa);
    }
    if (buildingId) return currentDesign;
  }
  const existing = getBuildingStoreys(currentDesign, buildingId);
  const floorCount = Math.max(
    existing.filter((storey) => !designGuards.is.mezzanineStorey(storey))
      .length,
    1,
  );
  const perFloorGfa = targetGfa / floorCount;
  const side = Math.sqrt(perFloorGfa);
  const squareStoreys = createSquareStoreysFromGfa(
    perFloorGfa,
    getEmptyBuildingPlacementOrigin(currentDesign, buildingId, side),
  );
  const firstSquare = squareStoreys[0];
  if (!firstSquare) return currentDesign;
  const source = getSiblingActivitySourceStorey(currentDesign, target?.id);
  const groundStorey = seedGroundStoreyGeometry(
    existing[0],
    firstSquare.shapes,
    source,
  );
  return setBuildingStoreysWithProgramSeed(
    currentDesign,
    buildingId,
    applyGroundFootprintToEmptyStoreys(existing, groundStorey),
  );
};

/**
 * Scale storey footprints uniformly around their own bounding-box center so
 * an offset second building does not slide toward the shared origin.
 */
const scaleStoreys = (storeys: Storey[], scale: number): Storey[] => {
  const points = storeys.flatMap((storey) =>
    storey.shapes.flatMap((shape) => shape.points),
  );
  if (!points.length) return storeys;
  const center = getCenter(...points);
  const toOrigin: Point2D = [-center[0], -center[1]];
  const back: Point2D = [center[0], center[1]];
  return storeys.map((storey) => ({
    ...storey,
    shapes: storey.shapes.map((shape) => {
      const points = movePoints(
        movePoints(shape.points, toOrigin).map((point) =>
          multiplyPoint2D(point, scale),
        ),
        back,
      );
      return {
        ...shape,
        points: isPolygon2D(points) ? points : shape.points,
      };
    }),
  }));
};

/**
 * True when {@link buildingId}'s solids overlap any sibling building.
 */
const wouldTargetOverlapSiblings = (
  design: BuildingDesign,
  buildingId: string | undefined,
): boolean => {
  const target = getBuildingById(design, buildingId);
  if (!target) return false;
  return design.buildings.some(
    (sibling) =>
      sibling.id !== target.id && doBuildingSolidsIntersect(target, sibling),
  );
};

/**
 * Keep {@link currentDesign} when the scaled result would overlap a sibling
 * ({@link doBuildingSolidsIntersect}). Persist would otherwise fail validation.
 */
const rejectOverlappingScale = (
  currentDesign: BuildingDesign,
  next: BuildingDesign,
  buildingId: string | undefined,
): ScaleBuildingToGfaResult => {
  if (next === currentDesign) return unchangedResult(currentDesign);
  if (wouldTargetOverlapSiblings(next, buildingId)) {
    return { design: currentDesign, status: 'overlap' };
  }
  return { design: next, status: 'applied' };
};

/**
 * Scale a building to {@link targetGfa} and report why the design did or did
 * not change. Use this when the caller must distinguish overlap from other
 * no-ops (tolerance, invalid GFA).
 */
export const applyScaleBuildingToGfa = (
  currentDesign: BuildingDesign,
  targetGfa: number,
  buildingId?: string,
): ScaleBuildingToGfaResult => {
  if (
    !targetGfa ||
    !guards.is.finiteNumber(targetGfa) ||
    guards.is.negativeNumber(targetGfa)
  ) {
    return unchangedResult(currentDesign);
  }

  if (buildingId && !getBuildingById(currentDesign, buildingId)) {
    return unchangedResult(currentDesign);
  }

  const targetBuildingId = resolveGfaTargetBuildingId(
    currentDesign,
    buildingId,
  );
  const storeys = getBuildingStoreys(currentDesign, targetBuildingId);
  const currentGfa = getGfaFromStoreys(storeys);

  // No geometry yet: create a square footprint on the target building only.
  if (isEmptyBuildingDesign(currentDesign, targetBuildingId)) {
    return rejectOverlappingScale(
      currentDesign,
      createSquareOnBuilding(currentDesign, targetGfa, targetBuildingId),
      targetBuildingId,
    );
  }

  if (!currentGfa || !guards.is.finiteNumber(currentGfa) || currentGfa <= 0) {
    return unchangedResult(currentDesign);
  }

  const ratio = targetGfa / currentGfa;
  if (Math.abs(1 - ratio) < DEFAULT_SCALE_TOLERANCE) {
    return unchangedResult(currentDesign);
  }

  const scaleFactor = Math.sqrt(ratio);
  if (!guards.is.finiteNumber(scaleFactor) || scaleFactor <= 0) {
    return unchangedResult(currentDesign);
  }

  return rejectOverlappingScale(
    currentDesign,
    setBuildingStoreys(
      currentDesign,
      targetBuildingId,
      scaleStoreys(storeys, scaleFactor),
    ),
    targetBuildingId,
  );
};

/**
 * Calculates a scaled design to match a target GFA (Gross Floor Area).
 * When {@link buildingId} is set, only that building is scaled so a second
 * building's GFA input cannot resize the first (DEV-2576). An omitted id fills
 * an empty sibling before scaling the first building, matching pen-close.
 * @param currentDesign - The current building design
 * @param targetGfa - The target GFA value
 * @param buildingId - Building to scale; omitted = empty sibling or first building
 * @returns The scaled design, or the input when scaling is not needed or invalid
 */
export const scaleBuildingToGfa = (
  currentDesign: BuildingDesign,
  targetGfa: number,
  buildingId?: string,
): BuildingDesign =>
  applyScaleBuildingToGfa(currentDesign, targetGfa, buildingId).design;
